import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isFunction } from 'lodash-es';
import SorterButton from '../SorterButton';
import useControlled from '../../hooks/useControlled';
import { PrimaryTableCol, SorterFun, SortInfo, TableRowData, TdPrimaryTableProps } from '../type';

export type SortMap = Record<string, SortInfo & { index: number }>;

export default function useSorter(props: TdPrimaryTableProps) {
  const { columns, multipleSort, sortIcon, hideSortTips, onChange, onDataChange, onSortChange } = props;
  const [originalData, setOriginalData] = useState<TableRowData[]>();
  const [innerSort, setInnerSort] = useState<SortInfo | SortInfo[]>();
  const [tSortInfo, setTSortInfo] = useControlled(props, 'sort', onSortChange);
  const [tData, setTData] = useControlled(props, 'data', onDataChange);
  const sorterFuncMap = getSorterFuncMap(columns);
  const sortArray = useMemo(() => {
    const sort = tSortInfo;
    if (!sort) {
      return [];
    }
    return Array.isArray(sort) ? sort : [sort];
  }, [tSortInfo]);
  const sortMap = useMemo(
    () =>
      sortArray.reduce((prev, cur, index) => {
        const newPrev = { ...prev };
        const { sortBy } = cur;
        newPrev[sortBy] = { index, ...cur };
        return newPrev;
      }, {}),
    [sortArray],
  );
  const isSortInfoSame = useCallback((a: SortInfo | SortInfo[], b: SortInfo | SortInfo[]) => {
    const tmpSortInfo = Array.isArray(a) ? a : [a];
    const tmpInnerSortInfo = Array.isArray(b) ? b : [b];
    if (tmpSortInfo.length && !b) return false;
    const item = tmpSortInfo[0];
    const result = tmpInnerSortInfo.find((t) => t.sortBy === item.sortBy);
    if (!result) {
      return false;
    }
    return item.descending === result.descending;
  }, []);

  const handleDataSort = (sortInfo: SortInfo | Array<SortInfo>) => {
    const sort = sortInfo;
    if (!sorterFuncMap || !Object.keys(sorterFuncMap).length) return;
    if (!originalData) {
      setOriginalData(tData);
    }
    const isEmptyArraySort = !sort || (sort instanceof Array && !sort.length);
    const isEmptyObjectSort = !(sort instanceof Array) && !sort?.sortBy;
    if (isEmptyArraySort || isEmptyObjectSort) {
      setTData(originalData, { trigger: 'sort' });
      return originalData;
    }
    const formattedSort = sort instanceof Array ? sort : [sort];
    // data 为受控属性，data.slice() 浅拷贝，防止 sort 导致原数据变异
    const newData: TableRowData[] = tData.slice().sort((a: TableRowData, b: TableRowData) => {
      let sortResult = 0;
      for (let i = 0, len = formattedSort.length; i < len; i++) {
        const item = formattedSort[i];
        const sortFunc = sorterFuncMap[item.sortBy];
        // 上一个排序字段值相同时才会进行下一个字段的大小对比
        if (sortResult === 0 && sortFunc) {
          sortResult = item.descending ? sortFunc(b, a) : sortFunc(a, b);
        } else {
          break;
        }
      }
      return sortResult;
    });
    // Data 变化返回的是数据引用，为避免死循环，特此检测排序数据前后是否相同，如果相同则不再触发事件
    if (JSON.stringify(newData) === JSON.stringify(tData)) return;
    setTData(newData, { trigger: 'sort' });
    return newData;
  };

  const handleSortHeaderClick = (col: PrimaryTableCol<TableRowData>, p: { descending: boolean }) => {
    let sortInfo: SortInfo | Array<SortInfo>;
    if (multipleSort) {
      sortInfo = getMultipleNextSort(col, p);
    } else {
      const sort = tSortInfo instanceof Array ? tSortInfo[0] : tSortInfo;
      sortInfo = getSingleNextSort(col, sort, p);
    }
    // 本地数据 data 排序，需同时抛出 data-change
    const newData = handleDataSort(sortInfo);
    const currentData = newData || tData;
    const currentDataSource = currentData;
    setTSortInfo(sortInfo, { currentDataSource, col });
    onChange?.({ sorter: sortInfo }, { currentData, trigger: 'sorter' });
    setInnerSort(sortInfo);
  };

  useEffect(() => {
    if (!tSortInfo || !Object.keys(tSortInfo).length || !tData.length) {
      return;
    }
    // isSortInfoSame 的两个参数顺序不可变
    if (!isSortInfoSame(tSortInfo, innerSort)) {
      handleDataSort(tSortInfo);
    }
    // eslint-disable-next-line
  }, [isSortInfoSame, tData, tSortInfo]);

  function getSingleNextSort(col: PrimaryTableCol, sortInfo: SortInfo, p: { descending: boolean }): SortInfo {
    // 排序字段和排序方式均相同，则取消排序
    if (sortInfo && sortInfo.sortBy === col.colKey && sortInfo.descending === p.descending) {
      return undefined;
    }
    return { sortBy: col.colKey, descending: p.descending };
  }

  function getMultipleNextSort(col: PrimaryTableCol<TableRowData>, p: { descending: boolean }): Array<SortInfo> {
    const sort = tSortInfo || [];
    if (!(sort instanceof Array)) {
      return;
    }
    const { colKey } = col;
    const result = [...sort];
    for (let i = 0, len = sort.length; i < len; i++) {
      if (sort[i].sortBy === colKey) {
        const next = getSingleNextSort(col, sort[i], p);
        if (next) {
          result[i] = next;
        } else {
          result.splice(i, 1);
        }
        return result;
      }
    }
    result.push({ sortBy: colKey, descending: p.descending });
    return result;
  }

  function getSortOrder(descending: boolean) {
    if (descending === undefined) return;
    return descending ? 'desc' : 'asc';
  }

  function getSorterFuncMap(columns: PrimaryTableCol[], map: { [key: string]: SorterFun<any> } = {}) {
    const newMap = { ...map };
    for (let i = 0, len = columns.length; i < len; i++) {
      const col = columns[i];
      if (isFunction(col.sorter)) {
        newMap[col.colKey] = col.sorter;
      }
      // 多级表头中的排序功能
      if (col.children?.length) {
        getSorterFuncMap(col.children, newMap);
      }
    }
    return newMap;
  }

  function renderSortIcon({ col }: { col: PrimaryTableCol<TableRowData>; colIndex: number }) {
    if (!col.sorter) return null;
    const sorterButtonsProps = {
      sortType: col.sortType,
      sortOrder: getSortOrder(sortMap[col.colKey]?.descending),
      sortIcon,
      hideSortTips,
    };
    return (
      <SorterButton
        key={`sorter-button-${col.colKey}`}
        {...sorterButtonsProps}
        onSortIconClick={(_, p: { descending: boolean }) => handleSortHeaderClick(col, p)}
      />
    );
  }

  return {
    renderSortIcon,
  };
}
